import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { guides, guideSlugs } from "@/mocks/guides";

const SITE_URL = (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) ? import.meta.env.VITE_SITE_URL : "https://pandabuy.tattoo";

export default function GuidePage() {
  let { slug } = useParams<{ slug: string }>();

  if (!slug) {
    slug = "pandabuy-sheet-guide";
  }
  const guide = slug ? guides[slug] : undefined;

  if (guide) {
    document.title = guide.title;
    window.scrollTo(0, 0);
  }

  if (!guide) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 pt-24">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
            Guide Not Found
          </h1>
          <p className="text-sm text-foreground-600 mb-6">
            The shopping guide you are looking for does not exist yet.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.subtitle,
            datePublished: "2026-06-29",
            dateModified: "2026-06-29",
            author: {
              "@type": "Organization",
              name: "Pandabuy Sheet",
            },
            publisher: {
              "@type": "Organization",
              name: "Pandabuy Sheet",
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE_URL}/guide/${slug}`,
            },
          }),
        }}
      />

      <section className="relative w-full min-h-[400px] md:min-h-[480px] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={guide.heroImageUrl}
            alt={guide.title}
            title={guide.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
        </div>

        <div className="relative z-10 w-full text-center px-4 md:px-6 pt-24 pb-14">
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl mx-auto">
            {guide.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
            {guide.subtitle}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            {guide.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 text-xs font-medium rounded-full bg-white/15 text-white/90 border border-white/20">
                {tag}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 mt-7">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full bg-white text-foreground-950 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Pandabuy Sheet
            </a>
          </div>
        </div>
      </section>

      <article className="py-14 md:py-20 px-4 md:px-10 bg-secondary-100">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-600 mb-3">
              SEO Guide &bull; {guide.readTime} read
            </span>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {guide.intro}
            </p>
          </div>

          {guide.sections.map((section, sIdx) => (
            <div key={sIdx} className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                {section.heading}
              </h2>

              {section.paragraphs.map((para, pIdx) => (
                <p
                  key={pIdx}
                  className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                  dangerouslySetInnerHTML={{
                    __html: para
                      .replace(
                        /\bPandabuy Sheet\b/g,
                        '<a href="/" class="text-primary-600 hover:text-primary-800 font-medium">Pandabuy Sheet</a>'
                      )
                      .replace(
                        /\bthe main Pandabuy Sheet hub\b/g,
                        '<a href="/" class="text-primary-600 hover:text-primary-800 font-medium">the main Pandabuy Sheet hub</a>'
                      )
                      .replace(
                        /\bthe main hub\b/g,
                        '<a href="/" class="text-primary-600 hover:text-primary-800 font-medium">the main hub</a>'
                      )
                      .replace(
                        /\bhomepage\b/g,
                        '<a href="/" class="text-primary-600 hover:text-primary-800 font-medium">homepage</a>'
                      )
                      .replace(
                        /\bKaspreadsheet\b/g,
                        '<a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" class="text-primary-600 hover:text-primary-800 font-medium">Kaspreadsheet</a>'
                      ),
                  }}
                ></p>
              ))}

              {section.listItems && (
                <ul className="space-y-2 mb-4 pl-5">
                  {section.listItems.map((item, lIdx) => (
                    <li key={lIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed list-disc">
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="mt-10 p-6 md:p-8 bg-background-50 rounded-lg border border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
              More Shopping Guides
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {guideSlugs
                .filter((s) => s !== slug)
                .map((gSlug) => (
                  <Link
                    key={gSlug}
                    to={`/guide/${gSlug}`}
                    className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors py-1.5 cursor-pointer"
                  >
                    <i className="ri-article-line text-sm w-5 h-5 flex items-center justify-center"></i>
                    {guides[gSlug].title}
                  </Link>
                ))}
            </div>
          </div>

          <div className="mt-12 p-6 md:p-8 bg-background-50 rounded-lg border border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
              Ready to Start Comparing?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 leading-relaxed">
              Explore all 14 categories on the Pandabuy Sheet today. No sign-up, no fees — just clean, structured product data that helps you make smarter buying decisions in seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                Back to Home
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full border border-foreground-300 text-foreground-700 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Open Pandabuy Sheet
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-background-50 rounded-lg p-4 text-center">
              <p className="font-heading text-2xl font-bold text-accent-600">{guide.readTime}</p>
              <p className="text-xs text-foreground-500 mt-1">Read Time</p>
            </div>
            <div className="bg-background-50 rounded-lg p-4 text-center">
              <p className="font-heading text-2xl font-bold text-accent-600">Jun 2026</p>
              <p className="text-xs text-foreground-500 mt-1">Last Updated</p>
            </div>
            <div className="bg-background-50 rounded-lg p-4 text-center">
              <p className="font-heading text-2xl font-bold text-accent-600">{guide.sections.length}</p>
              <p className="text-xs text-foreground-500 mt-1">Sections</p>
            </div>
            <div className="bg-background-50 rounded-lg p-4 text-center">
              <p className="font-heading text-2xl font-bold text-accent-600">14 Cat.</p>
              <p className="text-xs text-foreground-500 mt-1">Categories Linked</p>
            </div>
          </div>
        </div>
      </article>

      <section className="relative w-full min-h-[360px] md:min-h-[420px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Bright%20airy%20minimalist%20workspace%20with%20open%20laptop%20and%20coffee%20cup%2C%20warm%20cream%20walls%2C%20soft%20natural%20daylight%2C%20editorial%20photography%2C%20clean%20productive%20atmosphere%2C%20neutral%20warm%20tones%2C%20fresh%20modern%20aesthetic&width=1600&height=900&seq=guide-cta-univ&orientation=landscape"
            alt="Try the live Pandabuy Sheet"
            title="Pandabuy Sheet — Start Comparing Now"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/30 to-black/50"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 py-16">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Try the Live Spreadsheet
              </h2>
              <p className="mt-3 text-sm md:text-base text-white/80 max-w-md leading-relaxed">
                One click to access every comparison category on Pandabuy Sheet — free, no sign-up needed.
              </p>
            </div>
            <div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-3 px-6 py-3 text-sm font-semibold rounded-full bg-white text-foreground-950 hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>Pandabuy Sheet</span>
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-white">
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}